import { Injectable } from '@angular/core';
import {WebFormEntry, WebFormSigner, WebFormSubmission} from '../models/WebFormEntry';
import {UntypedFormArray, UntypedFormGroup} from '@angular/forms';
import {CustomFormControl} from '../components/controls/CustomFormControl';
import {FormPage, SoaMapping, SpecialMapping} from '../models/WebFormTemplate';
import {FormHelperService} from './form-helper.service';

@Injectable({
  providedIn: 'root'
})
export class FormSerializerService {

  constructor(private formHelper: FormHelperService) { }

  transformFormToJson(formId: string,
                      pdfUrl: string,
                      controls: Map<string, CustomFormControl>,
                      formPages: FormPage[],
                      specialMappings: SpecialMapping[] = [],
                      soaMappings: SoaMapping[] = []): string {
    const entries: WebFormEntry[] = [];
    const dynamicEntries = new Map<string, string>();
    const hiddenControls = new Set<string>();
    this.serializeRegularMappings(controls, dynamicEntries, formPages, entries, hiddenControls);
    this.serializeSpecialMappings(controls, dynamicEntries, specialMappings, entries);
    const webFormSigners = this.serializeSigners(controls, hiddenControls);

    const webFormSubmission = new WebFormSubmission();
    webFormSubmission.formId = formId;
    webFormSubmission.watermark = true;
    webFormSubmission.pdfUrl = pdfUrl;
    webFormSubmission.meta = this.getFormMeta(formId, controls);
    webFormSubmission.signers = webFormSigners;
    webFormSubmission.entries = entries;
    webFormSubmission.soaMappings = soaMappings ?? [];
    return JSON.stringify(webFormSubmission);
  }

  private getFormMeta(formId: string,
                      controls: Map<string, CustomFormControl>): string {
    switch (formId.toLowerCase()) {
      case 'assr-451':
        const control = controls.get('_property_type');
        if (!control) { return ''; }
        return control.element.option === '_real_property' ? 'real' : 'business';
      default:
        return 'real';
    }
  }

  private serializeRegularMappings(controls: Map<string, CustomFormControl>,
                                   dynamicEntries: Map<string, string>,
                                   formPages: FormPage[],
                                   entries: WebFormEntry[],
                                   hiddenControls: Set<string>): void {
    for (const formPage of formPages) {
      if (!this.formHelper.isFormPageVisible(formPage, controls)) {
        // Even if this page is not visible, the form fields are still expected.
        // So we will fill the mappings in with blank values
        this.serializeEmptyMappings(formPage.specialMappings, entries);
        // Additionally, track these hidden controls so that we know whether to include them
        // in separate mappings later
        for (const formElement of formPage.elements) {
          hiddenControls.add(formElement.id);
        }
        continue;
      }

      this.serializeFormGroup(formPage.formGroup, dynamicEntries, entries);
      this.serializeSpecialMappings(controls, dynamicEntries, formPage.specialMappings, entries);
    }
  }

  private serializeEmptyMappings(specialMappings: SpecialMapping[] = [],
                                 entries: WebFormEntry[]): void {
    if (specialMappings) {
      for (const specialMapping of specialMappings) {
        entries.push(new WebFormEntry('text', specialMapping.finalName, '', false, false));
      }
    }
  }

  private serializeSpecialMappings(controls: Map<string, CustomFormControl>,
                                   dynamicEntries: Map<string, string>,
                                   specialMappings: SpecialMapping[] = [],
                                   entries: WebFormEntry[]): void {
    // Then handle all special mappings
    if (specialMappings) {
      for (const specialMapping of specialMappings) {
        let formatted = specialMapping.format;

        for (let i = 0; i < specialMapping.fields.length; ++i) {
          // First look to see if the control value exists in the standard controls (those which appear in the form)
          const control = controls.get(specialMapping.fields[i]);
          if (control) {
            formatted = formatted.replace('{' + i + '}', control.value);
          } else {
            // It is possible this special mapping expects a dynamic entry. For example,
            // a control which splits its value into 1 or more new named pieces.
            const dynamicEntryValue = dynamicEntries.get(specialMapping.fields[i]);
            if (dynamicEntryValue) {
              formatted = formatted.replace('{' + i + '}', dynamicEntryValue);
            } else {
              // Otherwise there is no mapping which exists. Consume the variable so it doesn't display
              // on the preview.
              formatted = formatted.replace('{' + i + '}', '');
            }
          }
        }

        formatted = formatted
          .replace(/ +/g, ' ')
          .replace(/\n+/g, '\n');
        entries.push(new WebFormEntry('text', specialMapping.finalName, formatted, true, false));
        dynamicEntries.set(specialMapping.finalName, formatted);
      }
    }
  }

  private serializeSigners(controls: Map<string, CustomFormControl>, hiddenControls: Set<string>): WebFormSigner[] {
    const regexStr = /signer(\d+)/;
    const signers = new Set<string>();

    for (const [key] of controls.entries()) {
      const match = regexStr.exec(key);
      if (!match) { continue; }
      signers.add(`signer${match[1]}`);
    }

    const webFormSigners: WebFormSigner[] = [];
    for (const signer of signers) {
      if (hiddenControls.has(signer + '_email')) {
        continue; // This is a hidden control
      }

      const email = controls.get(signer + '_email')?.value;
      if (!email) { continue; }

      webFormSigners.push(new WebFormSigner(
        email,
        controls.get(signer + '_firstName')?.value,
        controls.get(signer + '_middleName')?.value,
        controls.get(signer + '_lastName')?.value,
        controls.get(signer + '_phone')?.value
      ));
    }

    return webFormSigners;
  }

  private serializeFormGroup(formGroup: UntypedFormGroup, dynamicEntries: Map<string, string>, entries: WebFormEntry[]): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof UntypedFormArray) {
        this.serializeTemplateArray(control as UntypedFormArray, dynamicEntries, entries);
      } else if (control instanceof UntypedFormGroup) {
        this.serializeFormGroup(control, dynamicEntries, entries);
      } else if (control instanceof CustomFormControl && !key.startsWith('_')) {
        const element = control.element;

        if (element.type === 'radio' || element.type === 'radio_large') {
          let index = 0;
          for (let i = 0; i < element.options.length; ++i) {
            if (element.options[i].id === control.value) {
              index = i;
              break;
            }
          }

          entries.push(new WebFormEntry('radio', element.id, index.toString(), true, element.isIgnoredField, ''));
        } else if (element.expander) {
          this.serializeWithExpander(control, dynamicEntries, entries, null);
        } else if (element.visibility && !element.isVisible) {
          entries.push(new WebFormEntry(element.type, element.id, '', false, element.isIgnoredField));
        } else if (element.type === 'checkbox') {
          if (control.value === true) {
            entries.push(new WebFormEntry(element.type, element.id, 'true', false, element.isIgnoredField)); // Exclude otherwise
          }
        } else if (element.type !== 'template') {
          this.serializeEntry(control, entries, control.element.id);
        }
      }
    });
  }

  private serializeEntry(control: CustomFormControl, entries: WebFormEntry[], id: string): void {
    const element = control.element;
    const isRequired = control.isRequired() && control.value?.length > 0;

    let serialized = control.value ? control.value.toString() : control.value;

    if (element.type === 'phone') {
      serialized = serialized.replace(/-/g, '');
    } else if (element.type === 'number') {
      const numberValue = parseFloat(control.value);
      serialized = control.value === null || isNaN(numberValue) ? '' : numberValue.toLocaleString('en-US');
    }

    if (!serialized || serialized.length === 0) {
      serialized = ' ';
    }

    entries.push(new WebFormEntry(element.type, id, serialized, isRequired, element.isIgnoredField));
  }

  private serializeWithExpander(control: CustomFormControl, dynamicEntries: Map<string, string>,
                                entries: WebFormEntry[], templateArrayIndex: number | null): void {
    const regexStr = new RegExp(control.element.expander.regex);
    const match = regexStr.exec(control.value);
    if (!match) { return; }

    for (let i = 1; i < match.length; ++i) {
      let subKey = control.element.expander.replacements[i - 1];
      if (templateArrayIndex) {
        subKey = subKey.replace('{i}', '' + templateArrayIndex);
      }
      entries.push(new WebFormEntry('text', subKey, match[i], true, false));
      dynamicEntries.set(subKey, match[i]);
    }
  }

  private serializeTemplateArray(formArray: UntypedFormArray, dynamicEntries: Map<string, string>, entries: WebFormEntry[]): void {
    let i = 1;
    for (const arrayControl of formArray.controls) {
      if (!(arrayControl instanceof UntypedFormGroup)) { continue; }

      const arrayFormGroup = arrayControl as UntypedFormGroup;

      Object.keys(arrayFormGroup.controls).forEach(key => {
        const customControl = arrayFormGroup.controls[key] as CustomFormControl;
        if (customControl.element.expander) {
          this.serializeWithExpander(customControl, dynamicEntries, entries, i);
        } else {
          const keyWithId = customControl.element.id.replace('{i}', '' + i);
          this.serializeEntry(customControl, entries, keyWithId);
          dynamicEntries.set(keyWithId, customControl.value);
        }
      });

      ++i;
    }
  }
}
